<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { Variant, Size } from '@@/types'

withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    outline?: boolean
  }>(),
  {
    variant: 'secondary',
    size: 'md',
  }
)

const attrs = useAttrs()

const isDisabled = computed(
  () => 'disabled' in attrs && attrs.disabled !== false
)
</script>

<template>
  <component
    :is="
      isDisabled && ($attrs.to || $attrs.href)
        ? 'span'
        : $attrs.to
          ? 'RouterLink'
          : $attrs.href
            ? 'a'
            : 'button'
    "
    class="line-clamp-1 flex items-center justify-center rounded border text-center transition-colors duration-100 hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 dark:ring-offset-gray-900"
    :class="{
      'text-sm h-8 px-4': size === 'sm',
      'h-10 px-6 text-base': size === 'md',
      'text-lg h-12 px-8': size === 'lg',

      'focus:ring-secondary-500 focus:ring-opacity-40':
        !isDisabled && variant === 'secondary',
      'focus:ring-primary-500 focus:ring-opacity-50':
        !isDisabled && variant === 'primary',
      'focus:ring-danger-500 focus:ring-opacity-50':
        !isDisabled && variant === 'danger',
      'focus:ring-warning-500 focus:ring-opacity-50':
        !isDisabled && variant === 'warning',
      'focus:ring-success-500 focus:ring-opacity-50':
        !isDisabled && variant === 'success',
      'focus:ring-info-400 focus:ring-opacity-50':
        !isDisabled && variant === 'info',

      'text-white bg-secondary-400 hover:bg-secondary-500':
        !isDisabled && !outline && variant === 'secondary',
      'text-white bg-primary-500 hover:bg-primary-600':
        !isDisabled && !outline && variant === 'primary',
      'text-white bg-danger-500 hover:bg-danger-600':
        !isDisabled && !outline && variant === 'danger',
      'text-white bg-warning-500 hover:bg-warning-600':
        !isDisabled && !outline && variant === 'warning',
      'text-white bg-success-500 hover:bg-success-600':
        !isDisabled && !outline && variant === 'success',
      'text-white bg-info-400 hover:bg-info-500':
        !isDisabled && !outline && variant === 'info',

      'border-transparent': !outline,

      'hover:text-white border-secondary-400 text-secondary-500 hover:border-transparent hover:bg-secondary-400':
        !isDisabled && outline && variant === 'secondary',
      'hover:text-white border-primary-500 text-primary-600 hover:border-transparent hover:bg-primary-500':
        !isDisabled && outline && variant === 'primary',
      'hover:text-white border-danger-500 text-danger-600 hover:border-transparent hover:bg-danger-500':
        !isDisabled && outline && variant === 'danger',
      'hover:text-white border-warning-500 text-warning-600 hover:border-transparent hover:bg-warning-500':
        !isDisabled && outline && variant === 'warning',
      'hover:text-white border-success-500 text-success-600 hover:border-transparent hover:bg-success-500':
        !isDisabled && outline && variant === 'success',
      'hover:text-white border-info-400 text-info-600 hover:border-transparent hover:bg-info-400':
        !isDisabled && outline && variant === 'info',

      'cursor-pointer': !isDisabled,
      'cursor-not-allowed bg-disabled-300 text-disabled-600 focus:ring-0':
        isDisabled,
    }"
  >
    <slot />
  </component>
</template>
